import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import constants from "../../constants";

import Loader from "../Common/Loader";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { cookieStorageUtility } from '../../utils/utilityFunctions';
import { checkRole } from '../Common/CheckRole';
import ReactPaginate from 'react-paginate';

const { serverUrl, ENCRYPTOR_KEY } = constants;
const NEGATIVE_DERIVATIVE_PATH = `${serverUrl}negativederivative`;
const authUserId = cookieStorageUtility.fetch('auth_user_id');
const encryptedToken = cookieStorageUtility.fetch('encrypted_' + ENCRYPTOR_KEY + '_token');

const NegativeDerivative = () => {
  // State hooks
  const [flag, setFlag] = useState(false);
  const [type, setType] = useState('error');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState("rules");
  const [showUILoader, setShowUILoader] = useState(false);
  const [rulesPage, setRulesPage] = useState(1);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [limit] = useState(10);
  const [derivativeTotal, setDerivativeTotal] = useState(0);
  const [rules, setRules] = useState([]);
  const [excludedProperties, setExcludedProperties] = useState([]);
  const [fileInput, setFileInput] = useState(null);

  // Helper function to display messages
  const setMessageFunc = useCallback((messageType, messageText) => {
    setFlag(true);
    setType(messageType);
    setMessage(messageText);
    setTimeout(() => setFlag(false), 3000);
  }, []);

  // Get authorization token
  const getToken = useCallback(() => {
    return btoa(`${authUserId}|${encryptedToken}`);
  }, []);

  // Toggle loader state
  const toggleLoad = useCallback(() => {
    setShowUILoader(prevState => !prevState);
  }, []);

  // Fetch data from API (used for both rules and properties)
  const fetchData = useCallback(async (page, type) => {
    toggleLoad();
    try {
      const response = await axios.post(`${NEGATIVE_DERIVATIVE_PATH}/index`, 
        {
          page: page,
          limit,
          type: type,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            token: getToken()
          }
        });

      if (response.status === 200) {
        const { data, pagination } = response.data;
        
        if (type === "rules") {
          setRules(data || []);
        } else {
          setExcludedProperties(data || []);
        }
        
        // Set the total records from pagination data
        if (pagination && pagination.total_records !== undefined) {
          setDerivativeTotal(pagination.total_records);
        }
        
        return true;
      } else {
        setMessageFunc('error', response.data.message || 'Error fetching data');
        return false;
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      setMessageFunc('error', `Error fetching ${type}. Please try again.`);
      return false;
    } finally {
      toggleLoad();
    }
  }, [limit, getToken, toggleLoad, setMessageFunc]);

  // Fetch rules data
  const fetchRules = useCallback(() => {
    return fetchData(rulesPage, "rules");
  }, [fetchData, rulesPage]);

  // Fetch excluded properties data
  const fetchExcludedProperties = useCallback(() => {
    return fetchData(propertiesPage, "properties");
  }, [fetchData, propertiesPage]);

  // Set active tab
  const setActiveTabFunc = useCallback((tabName) => {
    setActiveTab(tabName);
    if (tabName === "rules") {
      setRulesPage(1);
      // No need to fetch rules data since we're not displaying them
    } else {
      setPropertiesPage(1);
      // Immediate fetch for properties when tab changes
      setTimeout(() => fetchExcludedProperties(), 0);
    }
  }, [fetchExcludedProperties]);

  // Handle file upload
  const handleFiles = useCallback(async (event) => {
    const _file = event.target.files[0];
    
    // Reset the file input to allow selecting the same file again
    if (fileInput) {
      fileInput.value = '';
    }
    setFileInput(event.target);
    
    if (!_file) {
      return;
    }
    
    if (!_file.name.toLowerCase().endsWith(".csv")) {
      setMessageFunc("error", "Only CSV files are allowed");
      return;
    }
    
    let formData = new FormData();
    formData.append('file', _file);
    formData.append('type', activeTab);
    
    toggleLoad();
    
    try {
      const response = await axios.post(
        `${NEGATIVE_DERIVATIVE_PATH}/create`, 
        formData, 
        {
          headers: {
            Accept: 'application/json',
            token: getToken()
            // Note: Don't manually set Content-Type for multipart/form-data
            // The browser will set it automatically with the correct boundary
          }
        }
      );
      
      if (response.status === 200) {
        setMessageFunc('success', response.data.message || "Uploaded CSV successfully");
        // Refresh data after successful upload
        if (activeTab === "rules") {
          fetchRules();
        } else {
          fetchExcludedProperties();
        }
      } else {
        setMessageFunc('error', response.data.message || "Error uploading CSV");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage = error.response?.data?.message || "Error uploading CSV";
      setMessageFunc('error', errorMessage);
    } finally {
      toggleLoad();
    }
  }, [activeTab, getToken, toggleLoad, setMessageFunc, fetchRules, fetchExcludedProperties, fileInput]);

  // Download CSV data
  const downloadCsv = useCallback(async () => {
    toggleLoad();
    
    try {
      const response = await axios.post(
        `${NEGATIVE_DERIVATIVE_PATH}/latest`, 
        { type: activeTab },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            token: getToken()
          }
        }
      );
      
      if (response.status === 200) {
        if (response.data.data && response.data.data.length > 0) {
          const csvConfig = mkConfig({ 
            useKeysAsHeaders: true,
            filename: `negative_derivative_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`,
            showColumnHeaders: true
          });
          const csv = generateCsv(csvConfig)(response.data.data);
          download(csvConfig)(csv);
          
          setMessageFunc('success', response.data.message || 'CSV downloaded successfully');
        } else {
          setMessageFunc('error', 'No data available to download');
        }
      } else {
        setMessageFunc('error', response.data.message || 'Error downloading CSV');
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
      const errorMessage = error.response?.data?.message || "Error downloading CSV";
      setMessageFunc('error', errorMessage);
    } finally {
      toggleLoad();
    }
  }, [activeTab, getToken, toggleLoad, setMessageFunc]);

  // UI for Download and Upload CSV buttons
  const downloadUploadCsv = useCallback(() => {
    return (
      <div className="row topheading pad_lft_rht25 pad_top_bot10">
        <div className="col-xs-auto">
          <button 
            className="pad12 btn1 cursor_p" 
            onClick={downloadCsv}
            disabled={showUILoader}
          >
            Download CSV
          </button>
        </div>
        <div className="col-xs-auto">
          <label className="pad12 btn1 cursor_p btn" style={{ display: 'inline-block', marginBottom: 0 }}>
            Upload CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleFiles}
              style={{ display: 'none' }}
              disabled={showUILoader}
              className="btn btn-primary"
            />
          </label>
        </div>
      </div>
    );
  }, [downloadCsv, handleFiles, showUILoader]);

  // Format table headers
  const formatHeader = useCallback((header) => {
    return header
      .replace(/_/g, " ")
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (match) => match.toUpperCase());
  }, []);
  
  // Handler for pagination - properly memoized at component level
  const handlePageChange = useCallback((selectedItem, dataType) => {
    // ReactPaginate uses 0-based indexing, but our API expects 1-based indexing
    const newPage = selectedItem.selected + 1;
    console.log(`Page selected: ${selectedItem.selected}, sending page ${newPage} to API`);
    
    if (dataType === 'rules') {
      setRulesPage(newPage);
      fetchData(newPage, "rules");
    } else {
      setPropertiesPage(newPage);
      fetchData(newPage, "properties");
    }
  }, [fetchData]);
  
  // Pagination component
  const renderPagination = useCallback((currentPage, dataType) => {
    const pageCount = Math.ceil(derivativeTotal / limit) || 1;
    
    if (derivativeTotal <= 0) {
      return null;
    }
    
    return (
      <div className="cover tc margtop15">
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(selectedItem) => handlePageChange(selectedItem, dataType)}
          pageRangeDisplayed={2}
          pageCount={pageCount}
          previousLabel="< Prev"
          forcePage={currentPage - 1} // Convert from 1-based to 0-based for ReactPaginate
          containerClassName="pagination"
          activeClassName="active"
          marginPagesDisplayed={1}
          disabledClassName="disabled"
        />
        <div className="pagination-info text-center mt-2">
          Showing page {currentPage} of {pageCount}, Total {derivativeTotal} records
        </div>
      </div>
    );
  }, [derivativeTotal, limit, handlePageChange]);

  // Render rules tab content
  const renderRulesTab = useCallback(() => {
    if (showUILoader) {
      return <Loader />;
    }
    
    return (
      <React.Fragment>
        {downloadUploadCsv()}
        <div className="cover pad30 border_t_1 border_color_1">
          <div className="ovr tc marg_bottom10">
            <img src="/images/no_result.png" alt="No Properties Found" />
          </div>
          <div className="ovr tc fnt16 bld">Left For Future Enhancement</div>
        </div>
      </React.Fragment>
    );
  }, [showUILoader, downloadUploadCsv]);

  // Render properties table
  const renderProperties = useCallback(() => {
    if (excludedProperties.length === 0) {
      return (
        <div className="cover pad30 border_t_1 border_color_1">
          <div className="ovr tc marg_bottom10">
            <img src="/images/no_result.png" alt="No Properties Found" />
          </div>
          <div className="ovr tc fnt16 bld">No Excluded Properties Found</div>
        </div>
      );
    }
    
    const headers = ["S. No.", ...Object.keys(excludedProperties[0])];

    return (
      <div className="container_fluid pad_lft_rht20 padtop25 text-start">
        <div className="cover border_all_1 border_color_1">
          <div className="scrolly_auto">
            <div className="table_col table_fixed">
              <div className="table_row bg2_8">
                {headers.map((header, index) => (
                  <div key={index} className="tabcel align_middle pad10 tu bld">
                    {formatHeader(header)}
                  </div>
                ))}
              </div>

              {excludedProperties.map((property, rowIndex) => (
                <div key={rowIndex} className="table_row border_t_1 border_color_1 text-start">
                  <div className="tabcel align_middle pad10">
                    {rowIndex + 1 + (propertiesPage - 1) * limit}
                  </div>
                  {Object.keys(property).map((key, colIndex) => (
                    <div key={colIndex} className="tabcel align_middle pad10">
                      {property[key] || ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        {renderPagination(propertiesPage, 'properties')}
      </div>
    );
  }, [excludedProperties, propertiesPage, limit, renderPagination, formatHeader]);

  // Render excluded properties tab content
  const renderExcludedPropertiesTab = useCallback(() => {
    if (showUILoader) {
      return <Loader />;
    }
    
    return (
      <React.Fragment>
        {downloadUploadCsv()}
        {renderProperties()}
      </React.Fragment>
    );
  }, [downloadUploadCsv, renderProperties, showUILoader]);

  // Removed unused handlePageChange function since we're now using
  // the properly implemented function above

  // Load initial data on component mount and handle tab changes
  useEffect(() => {
    // Load data based on current active tab
    if (activeTab === "rules") {
      fetchRules();
    } else if (activeTab === "properties") {
      fetchExcludedProperties();
    }
  }, [activeTab, fetchRules, fetchExcludedProperties]);

  // Note: We've removed the useEffect that watched for propertiesPage changes
  // because we're now handling the fetch directly in the pagination handler

  // Authorization check - properly initialized with useState and setIsAuthorized
  const [isAuthorized, setIsAuthorized] = useState(true);
  
  useEffect(() => {
    // Check authorization once when component mounts
    if (false) {
      setIsAuthorized(false);
      setMessageFunc('error', 'You are not authorized to access this feature.');
    }
  }, [setMessageFunc]);
  
  if (!isAuthorized) {
    return <div className="container pad20 text-center">
      <div className="alert alert-danger">
        You are not authorized to access this feature.
      </div>
    </div>;
  }

  return (
    <div>
      <ul className="nav nav-tabs pad_lft_rht15 pad_top_bot10">
        {["rules", "properties"].map((tab) => (
          <li key={tab} className="nav-item d-full-height justify-content-center fnt18 tu">
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTabFunc(tab)}
              disabled={showUILoader}
            >
              {tab === "rules" ? "Rules" : "Excluded Properties"}
            </button>
          </li>
        ))}
      </ul>
      <div className="tab-panel">
        {activeTab === "rules" ? renderRulesTab() : renderExcludedPropertiesTab()}
      </div>
      {flag && (
        <div className={`alert_col posfixed pad10 tc fnt16 bld ${type === 'error' ? 'danger' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default NegativeDerivative;
